// =================================================================
// Login code utilities
//
// Resident codes follow the pattern FIRSTNAME-NNN, e.g. MARGARET-742.
// The name part comes from the resident's display name (first word,
// uppercased, alphabetic chars only). The number part is a random
// 3-digit suffix, checked against /brain_health/login_index/ for
// uniqueness before being claimed.
//
// On collision, we retry with a new random number. If we hit 5
// collisions in a row (extremely unlikely outside of stress tests),
// we escalate to a 4-digit suffix. After that, we give up rather
// than loop forever.
// =================================================================

import { ref, get, set } from 'firebase/database';
import { db } from './firebase.js';

const MAX_3DIGIT_ATTEMPTS = 5;
const MAX_4DIGIT_ATTEMPTS = 10;

/**
 * Extract the first-name prefix from a display name.
 * "Margaret Stevens"  →  "MARGARET"
 * "mary-anne smith"   →  "MARYANNE"
 * "  D'Angelo, Carmela" → "DANGELO"
 *
 * Strips non-letters, uppercases, caps at 20 chars for sanity.
 */
export function namePrefix(displayName) {
  const words = String(displayName || '').trim().split(/\s+/);
  const first = words[0] || '';
  const letters = first.replace(/[^a-zA-Z]/g, '').toUpperCase();
  if (letters.length === 0) return 'RESIDENT';
  return letters.slice(0, 20);
}

/**
 * Normalize a user-typed code to its canonical form.
 *
 * Forgives:
 *   - case ("margaret-742" → "MARGARET-742")
 *   - missing hyphen ("margaret742" → "MARGARET-742")
 *   - extra spaces ("  margaret 742 " → "MARGARET-742")
 *   - other non-alphanumerics (slashes, dots, etc.)
 *
 * Returns empty string for entirely-empty input.
 *
 * The hyphen is reinserted between the letter prefix and digit
 * suffix to match the canonical form stored in /login_index.
 */
export function normalizeLoginCode(raw) {
  const cleaned = String(raw || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (!cleaned) return '';

  // Split on the boundary between letters and digits.
  const match = cleaned.match(/^([A-Z]+)(\d+)$/);
  if (!match) {
    // Doesn't look like LETTERS+DIGITS — return as-is uppercase
    // for the lookup to fail cleanly with "Code not found".
    return cleaned;
  }
  return `${match[1]}-${match[2]}`;
}

/** Random integer in [min, max] inclusive. */
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Check whether a login code is already taken. Returns true if free.
 *
 * The login_index node maps loginCode → accountId. A missing entry
 * means the code is available.
 */
async function isCodeAvailable(code) {
  const snap = await get(ref(db, `brain_health/login_index/${code}`));
  return !snap.exists();
}

/**
 * Generate an unused login code for the given display name.
 *
 * Throws if we can't find an available code after multiple attempts.
 * In practice this only fires if a community has dozens of residents
 * with the same first name — at which point the admin should pick
 * a different code manually.
 *
 * @param {string} displayName - The resident's display name.
 * @returns {Promise<string>} A unique login code like "MARGARET-742".
 */
export async function generateLoginCode(displayName) {
  const prefix = namePrefix(displayName);

  // First: try 3-digit suffixes.
  for (let i = 0; i < MAX_3DIGIT_ATTEMPTS; i++) {
    const n = randomInt(100, 999);
    const candidate = `${prefix}-${n}`;
    if (await isCodeAvailable(candidate)) {
      return candidate;
    }
  }

  // Escalate to 4-digit suffixes if 3-digit kept colliding.
  for (let i = 0; i < MAX_4DIGIT_ATTEMPTS; i++) {
    const n = randomInt(1000, 9999);
    const candidate = `${prefix}-${n}`;
    if (await isCodeAvailable(candidate)) {
      return candidate;
    }
  }

  throw new Error(
    `Could not generate a unique code for "${displayName}" after ` +
    `${MAX_3DIGIT_ATTEMPTS + MAX_4DIGIT_ATTEMPTS} attempts. ` +
    `This is very unusual — please try a different first name or contact support.`
  );
}

/**
 * Atomically claim a login code by writing the index entry.
 * Note that RTDB doesn't have true transactions for this case
 * (we'd want to write both /accounts/{id} and /login_index/{code}
 * atomically), so we do best-effort: claim the code first, then
 * write the account. If the account write fails, the caller is
 * responsible for releasing the code.
 */
export async function claimLoginCode(code, accountId) {
  await set(ref(db, `brain_health/login_index/${code}`), accountId);
}

/**
 * Release a previously-claimed login code. Used to roll back if
 * the account write fails after the code was claimed.
 */
export async function releaseLoginCode(code) {
  await set(ref(db, `brain_health/login_index/${code}`), null);
}
